<template>
    <div>
        <rkm-area
            v-if="pacienteExists"
            title="Paciente"
            no-margin-top
        >
            <rkm-row>
                <rkm-col>
                    <rkm-row>
                        <rkm-col
                            align-self="center"
                            class="col-svg flex-grow-0"
                        >
                            <rkm-icon class="icon" icon="user"/>
                        </rkm-col>

                        <rkm-col class="flex-grow-1">
                            <h4>
                                {{ data.form.paciente.nome }}
                                <strong v-if="data.form.paciente.preferenciaNomeId === enums.preferenciaNome.NOME_SOCIAL">
                                    ({{ data.form.paciente.nomeSocial }})
                                </strong>
                            </h4>

                            <p>
                                <strong>Prontuário:</strong>
                                {{ data.form.paciente.prontuario }}
                            </p>
                            <p>
                                <template v-if="data.form.paciente.dataNascimento">
                                    <strong>Data de nascimento:</strong>
                                    {{ data.form.paciente.dataNascimento | formatDate }}
                                </template>
                            </p>
                            <p>
                                <template v-if="data.form.paciente.idade">
                                    {{ data.form.paciente.idade.anos }} anos,
                                    {{ data.form.paciente.idade.meses }} meses e
                                    {{ data.form.paciente.idade.dias }} dias,
                                </template>
                                {{ data.form.paciente.sexo.descricao }}
                            </p>
                        </rkm-col>
                    </rkm-row>

                    <rkm-row class="mt-2">
                        <rkm-col :cols="data.form.historicoAtendimento && data.form.historicoAtendimento.length > 0 ? 12 : 6">
                            <p v-if="data.form.paciente.isDesconhecido">
                                <strong>Características físicas:</strong>
                                {{ data.form.paciente.caracteristicasFisicas }}
                            </p>
                        </rkm-col>
                    </rkm-row>

                    <rkm-row class="mt-2">
                        <rkm-col :cols="data.form.historicoAtendimento && data.form.historicoAtendimento.length > 0 ? 12 : 6">
                            <p class="mb-1">
                                <strong>Motivo do atendimento:</strong> {{ data.form.recepcao.motivoAtendimento }}
                            </p>
                            <rkm-button
                                v-if="data.model.acidenteTrabalho && Number.isInteger(data.model.acidenteTrabalho.id)"
                                @click="$refs.ModalAcidenteTrabalho.open()"
                                block
                                class="justify-content-start"
                                color="primary"
                            >
                                <span v-if="data.model.acidenteTrabalho.tipoFormularioAcidenteTrabalho == enums.tipoFormularioAcidenteTrabalho.RAAT">RAAT Aberta</span>
                                <span v-else-if="data.model.acidenteTrabalho.tipoFormularioAcidenteTrabalho == enums.tipoFormularioAcidenteTrabalho.CAT">CAT Aberta</span>
                                <span v-else>CAT/RAAT Aberta</span>
                            </rkm-button>

                            <rkm-button
                                v-if="data.model.gestacao && data.model.gestacao.gestante"
                                @click="handleGestanteScroll"
                                block
                                class="ml-0 justify-content-start"
                                color="danger gestante-border gestante-base gestante-color"
                            >
                                Gestante: {{ data.model.gestacao.idadeGestacional.semanas }} semana(s) e {{ data.model.gestacao.idadeGestacional.dias }} dia(s)
                            </rkm-button>

                            <rkm-button
                                v-if="data.form.paciente.pcd"
                                block
                                class="ml-0 justify-content-start"
                                color="secondary"
                            >
                                PcD - {{ getPCDDescricao }}
                            </rkm-button>
                        </rkm-col>
                    </rkm-row>
                    <rkm-row class="mt-2 mb-2 ">

                        <rkm-col class="d-flex">
                            <rkm-form-select
                                v-model="data.form.prioridadeId"
                                name="Prioridade"
                                :options="prioridades"
                                valueField="id"
                                textField="nome"
                                :validations="validations.prioridade"
                                show-default-option
                                :disabled="!isAtendimentoPreferencial"
                                class="m-0"
                            >
                                <rkm-input-group-prepend slot="prepend">
                                    <rkm-input-group-text>
                                        <rkm-form-checkbox v-model="isAtendimentoPreferencial">
                                            Atendimento preferencial
                                        </rkm-form-checkbox>
                                    </rkm-input-group-text>
                                </rkm-input-group-prepend>
                            </rkm-form-select>
                        </rkm-col>
                    </rkm-row>
                </rkm-col>
            </rkm-row>
        </rkm-area>

        <modal-acidente-trabalho ref="ModalAcidenteTrabalho" :data="data" :disabled="disabledAcidenteTrabalho" />
    </div>
</template>

<script>
// Components.
import ModalAcidenteTrabalho from '@/components/pronto-atendimento/components/acidente-trabalho/Modal'

// Enums.
import preferenciaNome from '@/enums/cadastro/cidadao/preferenciaNome'
import deficiencia from '@/enums/cadastro/cidadao/deficienciaBase'
import tipoFormularioAcidenteTrabalho from '@/enums/pronto-atendimento/tipoFormularioAcidenteTrabalho'
import sexo from '@/enums/cadastro/cidadao/sexo'
import prioridade from '@/enums/painel-senha/prioridade'

// Filters
import formats from '@/filters/formats'

export default {
    components: { ModalAcidenteTrabalho },
    computed: {
        pacienteExists() {
            return this.data.form && this.data.form.paciente
        },
        getPCDDescricao() {
            return this.data.form.paciente.pcd.deficienciaBaseId === this.enums.deficiencia.OUTRA ?
                this.data.form.paciente.pcd.deficienciaOutro : this.data.form.paciente.pcd.descricao
        },
        prioridades() {
            if (this.isPacienteMasculino) {
                return this.data.form.prioridades.filter(p => p.id !== this.enums.prioridade.GESTANTE)
            }
            return this.data.form.prioridades
        },
        isPacienteMasculino() {
            return this.data.form.paciente.sexo.id === this.enums.sexo.MASCULINO
        },
        validations() {
            return {
                prioridade: { required: this.isAtendimentoPreferencial },
            }
        },
    },
    created() {
        this.isAtendimentoPreferencial = !!this.data.form.prioridadeId;
    },
    filters: {
        formatDate: formats.formatDate,
    },
    data: () => ({
        enums: { preferenciaNome, deficiencia, tipoFormularioAcidenteTrabalho, sexo, prioridade },
        isAtendimentoPreferencial: false,
    }),
    methods: {
        handleGestanteScroll() {
            const element = document.querySelector('#gestante')
            element.scrollIntoView({ behavior: 'smooth', block: 'center' })
        },
    },
    props: {
        data: { type: Object, required: true },
        disabledAcidenteTrabalho: { type: Boolean, default: false }
    },
    watch: {
        'isAtendimentoPreferencial': function (value) {
            if (!value) {
                this.data.form.prioridadeId = null
            }
        },
    }
}
</script>

<style scoped>
p {
    margin: 0px;
}
.col-svg {
    text-align: center !important;
}
.icon {
    width: auto !important;
    height: 96px !important;
    color: #007bff !important;
}
</style>